import React from "react";

const styles = {
  page: {
    minHeight: "100vh",
    background: "#EEF3FC",
    color: "#1E293B",
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    padding: "48px 16px",
    display: "flex",
    justifyContent: "center",
  },
  container: {
    maxWidth: 900,
    width: "100%",
    background: "#FFFFFF",
    border: "1px solid #E2E8F0",
    borderRadius: 18,
    padding: "40px 48px",
    boxShadow: "0 24px 64px rgba(29, 78, 216, 0.10)",
  },
  h1: {
    fontSize: "1.85rem",
    fontWeight: 800,
    color: "#0B1E3F",
    marginBottom: 6,
    letterSpacing: "-0.5px",
  },
  subtitle: {
    fontSize: "0.92rem",
    color: "#64748B",
    marginBottom: 24,
  },
  version: {
    display: "inline-block",
    background: "#DBEAFE",
    color: "#1D4ED8",
    border: "1px solid #93C5FD",
    borderRadius: 6,
    padding: "2px 10px",
    fontSize: "0.7rem",
    fontWeight: 700,
    marginBottom: 16,
    letterSpacing: "0.5px",
  },
  sectionTitle: {
    fontSize: "1.15rem",
    fontWeight: 700,
    color: "#1D4ED8",
    marginTop: 36,
    marginBottom: 14,
    paddingBottom: 8,
    borderBottom: "1px solid #E2E8F0",
  },
  subsectionTitle: {
    fontSize: "0.95rem",
    fontWeight: 700,
    color: "#0F172A",
    marginTop: 22,
    marginBottom: 8,
  },
  p: {
    fontSize: "0.88rem",
    lineHeight: 1.7,
    color: "#334155",
    marginBottom: 12,
  },
  code: {
    background: "#F1F5FB",
    border: "1px solid #DCE6F5",
    borderRadius: 10,
    padding: "14px 16px",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.75rem",
    color: "#1E3A8A",
    lineHeight: 1.7,
    overflowX: "auto",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    marginBottom: 14,
  },
  inlineCode: {
    background: "#EFF6FF",
    border: "1px solid #DCE6F5",
    borderRadius: 4,
    padding: "1px 6px",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.8rem",
    color: "#1D4ED8",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "0.8rem",
    marginBottom: 18,
    border: "1px solid #E2E8F0",
    borderRadius: 10,
    overflow: "hidden",
  },
  th: {
    background: "#F5F9FF",
    color: "#1D4ED8",
    textAlign: "left",
    padding: "10px 12px",
    fontWeight: 700,
    fontSize: "0.72rem",
    letterSpacing: "0.3px",
    borderBottom: "1px solid #E2E8F0",
  },
  td: {
    padding: "10px 12px",
    borderBottom: "1px solid #F1F5F9",
    color: "#334155",
    verticalAlign: "top",
  },
  tdMono: {
    padding: "10px 12px",
    borderBottom: "1px solid #F1F5F9",
    color: "#1D4ED8",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.75rem",
    verticalAlign: "top",
  },
  ul: {
    paddingLeft: 20,
    marginBottom: 14,
  },
  li: {
    fontSize: "0.88rem",
    lineHeight: 1.7,
    color: "#334155",
    marginBottom: 6,
  },
  callout: {
    background: "#FFFBEB",
    border: "1px solid #FBBF24",
    borderLeft: "4px solid #F59E0B",
    borderRadius: 8,
    padding: "12px 16px",
    marginBottom: 18,
    fontSize: "0.84rem",
    color: "#92400E",
    lineHeight: 1.6,
  },
  badge: {
    display: "inline-block",
    background: "#ECFDF5",
    color: "#047857",
    border: "1px solid #6EE7B7",
    borderRadius: 4,
    padding: "1px 8px",
    fontSize: "0.68rem",
    fontWeight: 700,
    marginRight: 6,
    fontFamily: "'JetBrains Mono', monospace",
  },
  divider: {
    height: 1,
    background: "#E2E8F0",
    margin: "28px 0",
    border: "none",
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  downloadBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    background: "#1D4ED8",
    color: "#FFFFFF",
    border: "none",
    borderRadius: 10,
    padding: "10px 18px",
    fontSize: "0.82rem",
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: "0 6px 18px rgba(29, 78, 216, 0.25)",
    transition: "all 0.15s ease",
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    textDecoration: "none",
  },
};

const endpoints = [
  { method: "POST", route: "/customer-dlr-url", purpose: "Add a DLR webhook URL." },
  { method: "PUT", route: "/customer-dlr-url/:id", purpose: "Update an existing webhook configuration." },
  { method: "DELETE", route: "/customer-dlr-url/:id", purpose: "Delete a webhook configuration." },
  { method: "GET", route: "/customer-dlr-url?page=1&limit=10", purpose: "List webhook configurations." },
];

const requestFields = [
  { field: "VER", required: "Yes", desc: "API version. Use 1.0." },
  { field: "SERVICETYPE", required: "Yes", desc: "SMS, RCS, WHATSAPP, or WORKFLOW." },
  { field: "TEMPLATENAME", required: "Yes", desc: "Exact approved template name (or configured flow name for WORKFLOW) for the selected service." },
  { field: "STATICVARS", required: "No", desc: "Static values shared by recipients. Use an empty string when unused." },
  { field: "isblacklistfilter", required: "Yes", desc: "true enables configured blacklist filtering; false skips it." },
  { field: "dlr_url", required: "Conditional*", desc: "HTTPS callback URL for this request. The saved panel URL is used when this field is omitted." },
  { field: "DATA", required: "Yes", desc: "Array containing 1 to 200 recipient objects." },
  { field: "DATA[].M", required: "Yes", desc: "Recipient mobile number in international digit format." },
  { field: "DATA[].V", required: "When used", desc: "Template variables separated by ~ in approved placeholder order." },
];

const smsStatuses = [
  { status: "DELIVRD", meaning: "Message delivered successfully." },
  { status: "FAILED", meaning: "Provider/operator reported a delivery failure." },
  { status: "EXPIRED", meaning: "Delivery validity period expired." },
  { status: "UNDELIV / UNDELIVERED", meaning: "Message could not be delivered." },
];

const rcsStatuses = [
  { status: "SENT", meaning: "Message accepted/sent through the RCS provider." },
  { status: "DELIVERED", meaning: "Message delivered to the recipient device." },
  { status: "READ", meaning: "Recipient opened/read the message." },
  { status: "CLICK", meaning: "Recipient clicked a tracked RCS action or link." },
  { status: "FAILED", meaning: "RCS processing or delivery failed." },
];

const whatsappStatuses = [
  { status: "sent", meaning: "Message sent to the WhatsApp platform." },
  { status: "delivered", meaning: "Message delivered to the recipient device." },
  { status: "read", meaning: "Recipient read the message." },
  { status: "failed", meaning: "Message processing or delivery failed." },
];

const httpResults = [
  { code: "2xx", action: "Store request_id and await asynchronous DLR callbacks." },
  { code: "400", action: "Correct payload fields, template values, phone data, or recipient count." },
  { code: "401 / 403", action: "Verify API key, permission, and any source-IP restriction." },
  { code: "402", action: "Add sufficient balance or reduce recipients, then submit a new request." },
  { code: "429", action: "Retry after a delay using exponential backoff." },
  { code: "5xx", action: "Retry safely with exponential backoff and idempotency controls." },
];

// ─── DOWNLOAD HELPER ──────────────────────────────────────────────────────────
const PDF_URL =
  "https://onespace.fabmediatech.com/Download/FabMedia_Customer_Send_APIGuide.pdf";
const PDF_FILENAME = "FabMedia_Customer_Send_APIGuide.pdf";

const downloadPdf = async () => {
  try {
    const response = await fetch(PDF_URL, { method: "GET" });
    if (!response.ok) throw new Error(`Download failed: ${response.status}`);

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = PDF_FILENAME;
    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.error("PDF download error:", err);
    // Fallback: open in a new tab so user can save manually
    window.open(PDF_URL, "_blank", "noopener,noreferrer");
  }
};

const ApiGuidePage = () => {
  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.headerRow}>
          <span style={styles.version}>v1.1 — September 2026</span>
          <button
            type="button"
            onClick={downloadPdf}
            style={styles.downloadBtn}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#1E40AF";
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#1D4ED8";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            ⬇ Download PDF
          </button>
        </div>

        <h1 style={styles.h1}>Customer Send API</h1>
        <div style={styles.subtitle}>SMS, RCS, WhatsApp and WORKFLOW integration guide</div>

        {/* Production endpoint summary */}
        <table style={styles.table}>
          <tbody>
            <tr>
              <td style={styles.td}><strong>Production endpoint</strong></td>
              <td style={styles.tdMono}>https://onespace.fabmediatech.com/api/customer/create-data</td>
            </tr>
            <tr>
              <td style={styles.td}><strong>Method</strong></td>
              <td style={styles.tdMono}>POST</td>
            </tr>
            <tr>
              <td style={styles.td}><strong>Authentication</strong></td>
              <td style={styles.tdMono}>x-api-key request header</td>
            </tr>
            <tr>
              <td style={styles.td}><strong>Content type</strong></td>
              <td style={styles.tdMono}>application/json</td>
            </tr>
            <tr>
              <td style={styles.td}><strong>API version</strong></td>
              <td style={styles.tdMono}>1.0</td>
            </tr>
            <tr>
              <td style={styles.td}><strong>Document version</strong></td>
              <td style={styles.tdMono}>1.1 - September 2026</td>
            </tr>
          </tbody>
        </table>

        {/* 1. Integration overview */}
        <h2 style={styles.sectionTitle}>1. Integration overview</h2>
        <p style={styles.p}>
          The Customer Send API provides one endpoint for sending approved template messages through SMS, RCS, or
          WhatsApp. Select the channel with <span style={styles.inlineCode}>SERVICETYPE</span>, provide the approved
          template name, and submit 1 to 200 recipient records.
        </p>
        <p style={styles.p}>
          A successful response confirms asynchronous acceptance. Final recipient outcomes are delivered to the
          configured DLR webhook.
        </p>

        <h3 style={styles.subsectionTitle}>Authentication</h3>
        <pre style={styles.code}>{`POST /api/customer/create-data HTTP/1.1
Host: onespace.fabmediatech.com
x-api-key: YOUR_API_KEY
Content-Type: application/json`}</pre>
        <div style={styles.callout}>
          Keep the API key on a secure server. Do not expose it in browser code, public repositories, screenshots,
          URLs, or logs.
        </div>

        <h3 style={styles.subsectionTitle}>Integration sequence</h3>
        <ul style={styles.ul}>
          <li style={styles.li}>Generate an API key and enable the required SMS, RCS, and/or WhatsApp service permissions.</li>
          <li style={styles.li}>Configure a publicly reachable HTTPS DLR webhook URL.</li>
          <li style={styles.li}>Use the exact approved template name for the selected channel.</li>
          <li style={styles.li}>Send recipient mobile numbers in international format using digits only.</li>
          <li style={styles.li}>Store every returned <span style={styles.inlineCode}>request_id</span> for reconciliation.</li>
          <li style={styles.li}>Accept duplicate or out-of-order DLR events safely and return HTTP 2xx quickly.</li>
        </ul>
        <div style={styles.callout}>
          <strong>Mandatory DLR requirement:</strong> Provide <span style={styles.inlineCode}>dlr_url</span> in the
          send payload or configure the webhook in the panel using DLR URL Management. The configured URL must accept
          HTTP POST JSON callbacks for SMS, RCS, and WhatsApp.
        </div>

        {/* 2. Common request format */}
        <h2 style={styles.sectionTitle}>2. Common request format</h2>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Field</th>
              <th style={styles.th}>Required</th>
              <th style={styles.th}>Description</th>
            </tr>
          </thead>
          <tbody>
            {requestFields.map((row) => (
              <tr key={row.field}>
                <td style={styles.tdMono}>{row.field}</td>
                <td style={styles.td}>{row.required}</td>
                <td style={styles.td}>{row.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p style={styles.p}>
          Variable values are applied sequentially from left to right. Example:
          <span style={styles.inlineCode}> Sample User~Mumbai</span> maps to template variable 1 and variable 2. When
          no variables are required, send <span style={styles.inlineCode}>V</span> as an empty string.
        </p>

        {/* 3. SMS request */}
        <h2 style={styles.sectionTitle}>3. SMS request</h2>
        <p style={styles.p}>
          Set <span style={styles.inlineCode}>SERVICETYPE</span> to <span style={styles.inlineCode}>SMS</span> and use
          the exact approved SMS template name.
        </p>
        <pre style={styles.code}>{`{
  "VER": "1.0",
  "SERVICETYPE": "SMS",
  "TEMPLATENAME": "SMS_ORDER_UPDATE",
  "STATICVARS": "",
  "isblacklistfilter": false,
  "dlr_url": "https://client.example.com/webhooks/sms-dlr",
  "DATA": [
    { "M": "910000000001", "V": "Sample User~Mumbai" }
  ]
}`}</pre>

        {/* 4. RCS request */}
        <h2 style={styles.sectionTitle}>4. RCS request</h2>
        <p style={styles.p}>
          Set <span style={styles.inlineCode}>SERVICETYPE</span> to <span style={styles.inlineCode}>RCS</span>. The
          approved template, bot/route, applicable rate, and sufficient balance must be available for the
          authenticated account.
        </p>
        <pre style={styles.code}>{`{
  "VER": "1.0",
  "SERVICETYPE": "RCS",
  "TEMPLATENAME": "RCS_ORDER_UPDATE",
  "STATICVARS": "",
  "isblacklistfilter": false,
  "dlr_url": "https://client.example.com/webhooks/rcs-dlr",
  "DATA": [
    { "M": "910000000001", "V": "Sample User~Mumbai" }
  ]
}`}</pre>

        {/* 5. WhatsApp request */}
        <h2 style={styles.sectionTitle}>5. WhatsApp request</h2>
        <p style={styles.p}>
          Set <span style={styles.inlineCode}>SERVICETYPE</span> to{" "}
          <span style={styles.inlineCode}>WHATSAPP</span> and use the exact approved WhatsApp template name.
        </p>
        <pre style={styles.code}>{`{
  "VER": "1.0",
  "SERVICETYPE": "WHATSAPP",
  "TEMPLATENAME": "WA_ORDER_UPDATE",
  "STATICVARS": "",
  "isblacklistfilter": false,
  "dlr_url": "https://client.example.com/webhooks/whatsapp-dlr",
  "DATA": [
    { "M": "910000000001", "V": "Sample User~Mumbai" }
  ]
}`}</pre>

        {/* 6. DLR webhook configuration */}
        <h2 style={styles.sectionTitle}>6. DLR webhook configuration</h2>
        <p style={styles.p}>
          The callback URL may be supplied per send request through <span style={styles.inlineCode}>dlr_url</span>, or
          saved through DLR URL Management in the user panel. When a valid per-request URL is present, it is used for
          that request; otherwise, the enabled panel webhook is used.
        </p>
        <p style={styles.p}>
          * The webhook must accept POST requests with a JSON body for every enabled service: SMS, RCS, and WhatsApp.
        </p>

        <h3 style={styles.subsectionTitle}>Callback management base URL</h3>
        <p style={styles.p}>
          <span style={styles.inlineCode}>https://onespace.fabmediatech.com/rcsapi/api/webhook</span>
        </p>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Method</th>
              <th style={styles.th}>Route</th>
              <th style={styles.th}>Purpose</th>
            </tr>
          </thead>
          <tbody>
            {endpoints.map((ep) => (
              <tr key={ep.method + ep.route}>
                <td style={styles.tdMono}>{ep.method}</td>
                <td style={styles.tdMono}>{ep.route}</td>
                <td style={styles.td}>{ep.purpose}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h3 style={styles.subsectionTitle}>Create example</h3>
        <pre style={styles.code}>{`{
  "DlrUrl": "https://client.example.com/webhooks/message-dlr",
  "IsDlrUrlForAPI": true,
  "Both": false
}`}</pre>

        <h3 style={styles.subsectionTitle}>Update example</h3>
        <pre style={styles.code}>{`{
  "DlrUrl": "https://client.example.com/webhooks/message-dlr-v2",
  "IsDlrUrlForAPI": true,
  "Both": true
}`}</pre>
        <p style={styles.p}>
          Select <strong>API</strong> when the webhook is intended for API-originated traffic. Select{" "}
          <strong>Both</strong> when the same URL should receive API and bulk/campaign traffic.
        </p>

        <h3 style={styles.subsectionTitle}>Webhook receiver requirements</h3>
        <ul style={styles.ul}>
          <li style={styles.li}>Use HTTPS with a valid TLS certificate and a publicly reachable hostname.</li>
          <li style={styles.li}>Accept <span style={styles.inlineCode}>Content-Type: application/json</span> and return HTTP 200–299 promptly.</li>
          <li style={styles.li}>Process duplicate events idempotently and tolerate out-of-order status progression.</li>
          <li style={styles.li}>Store <span style={styles.inlineCode}>request_id</span> / <span style={styles.inlineCode}>message_id</span>, recipient number, status, and callback timestamp for reconciliation.</li>
        </ul>

        {/* 7. SMS delivery reports */}
        <h2 style={styles.sectionTitle}>7. SMS delivery reports</h2>
        <p style={styles.p}>
          SMS operator statuses are forwarded in the <span style={styles.inlineCode}>stat</span> field. The value can
          vary by operator. Integrations must compare statuses case-insensitively and preserve the received value for
          audit.
        </p>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Meaning</th>
            </tr>
          </thead>
          <tbody>
            {smsStatuses.map((row) => (
              <tr key={row.status}>
                <td style={styles.tdMono}>{row.status}</td>
                <td style={styles.td}>{row.meaning}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h3 style={styles.subsectionTitle}>SMS DLR payload</h3>
        <pre style={styles.code}>{`{
  "apireceive_id": 1001,
  "request_id": "11111111-2222-4333-8444-555555555555",
  "errorcode": "",
  "date": "2026-09-18T09:00:00.000Z",
  "stat": "DELIVRD",
  "phno": "+910000000001"
}`}</pre>
        <p style={styles.p}>
          For unsuccessful delivery, <span style={styles.inlineCode}>errorcode</span> contains the available
          operator/provider error value. One request can generate a separate DLR for each recipient.
        </p>

        {/* 8. RCS delivery reports */}
        <h2 style={styles.sectionTitle}>8. RCS delivery reports</h2>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Meaning</th>
            </tr>
          </thead>
          <tbody>
            {rcsStatuses.map((row) => (
              <tr key={row.status}>
                <td style={styles.tdMono}>{row.status}</td>
                <td style={styles.td}>{row.meaning}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h3 style={styles.subsectionTitle}>RCS DLR payload</h3>
        <pre style={styles.code}>{`{
  "Message": "RCS DLR report",
  "Apirequest_id": 2001,
  "request_id": "22222222-3333-4444-8555-666666666666",
  "Status": "DELIVERED",
  "Ph_no": "910000000001",
  "UpdatedAt": "2026-09-18T05:18:16.998Z",
  "Error": null,
  "Timestamp": "2026-09-18T05:18:16.999Z"
}`}</pre>

        {/* 9. WhatsApp delivery reports */}
        <h2 style={styles.sectionTitle}>9. WhatsApp delivery reports</h2>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Meaning</th>
            </tr>
          </thead>
          <tbody>
            {whatsappStatuses.map((row) => (
              <tr key={row.status}>
                <td style={styles.tdMono}>{row.status}</td>
                <td style={styles.td}>{row.meaning}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h3 style={styles.subsectionTitle}>WhatsApp DLR payload</h3>
        <pre style={styles.code}>{`{
  "object": "whatsapp_business_account",
  "entry": [
    {
      "id": "100000000000001",
      "changes": [
        {
          "value": {
            "messaging_product": "whatsapp",
            "metadata": {
              "display_phone_number": "910000000002",
              "phone_number_id": "100000000000002"
            },
            "contacts": [
              {
                "wa_id": "910000000001",
                "user_id": "IN.EXAMPLE_USER_001"
              }
            ],
            "statuses": [
              {
                "id": "wamid.EXAMPLE_MESSAGE_ID_001",
                "status": "delivered",
                "timestamp": "1789714656",
                "recipient_id": "910000000001",
                "recipient_user_id": "IN.EXAMPLE_USER_001",
                "pricing": {
                  "billable": true,
                  "pricing_model": "PMP",
                  "category": "utility",
                  "type": "regular"
                }
              }
            ]
          },
          "field": "messages"
        }
      ]
    }
  ],
  "message_id": "wamid.EXAMPLE_MESSAGE_ID_001",
  "status": "delivered",
  "customer_id": "1001"
}`}</pre>
        <p style={styles.p}>
          WhatsApp may include additional provider metadata. The receiver should tolerate additional fields and process
          the message identifier, recipient, status, and timestamp relevant to the integration.
        </p>

        {/* 10. API response */}
        <h2 style={styles.sectionTitle}>10. API response and reconciliation</h2>
        <p style={styles.p}>
          An accepted response confirms that the request entered asynchronous processing. It does not confirm final
          delivery.
        </p>
        <pre style={styles.code}>{`{
  "status": true,
  "message": "Request accepted for processing",
  "request_id": "33333333-4444-4555-8666-777777777777",
  "service_type": "RCS",
  "accepted_at": "2026-09-18T10:33:45.220Z"
}`}</pre>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>HTTP result</th>
              <th style={styles.th}>Recommended action</th>
            </tr>
          </thead>
          <tbody>
            {httpResults.map((row) => (
              <tr key={row.code}>
                <td style={styles.tdMono}>{row.code}</td>
                <td style={styles.td}>{row.action}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* 11. Production checklist */}
        <h2 style={styles.sectionTitle}>11. Production checklist</h2>
        <ul style={styles.ul}>
          <li style={styles.li}>API key generated, authorized for required services, and stored securely.</li>
          <li style={styles.li}>Approved template name verified separately for SMS, RCS, and WhatsApp.</li>
          <li style={styles.li}>Each request contains 1 to 200 DATA records.</li>
          <li style={styles.li}>Mobile numbers use international digit format and template variables follow approved order.</li>
          <li style={styles.li}>* A valid DLR webhook is supplied through <span style={styles.inlineCode}>dlr_url</span> or enabled in the panel.</li>
          <li style={styles.li}>* The webhook accepts POST JSON callbacks for SMS, RCS, and WhatsApp.</li>
          <li style={styles.li}>Webhook returns HTTP 2xx quickly and safely handles duplicate/out-of-order events.</li>
          <li style={styles.li}><span style={styles.inlineCode}>request_id</span> and recipient identifiers are stored for reconciliation.</li>
          <li style={styles.li}>API keys and sensitive message content are excluded from logs and support messages.</li>
        </ul>

        {/* Support */}
        <h2 style={styles.sectionTitle}>Support information</h2>
        <p style={styles.p}>
          When reporting an issue, provide the <span style={styles.inlineCode}>request_id</span>, service type,
          template name, approximate timestamp, affected mobile number, HTTP status, and sanitized response. Do not
          share the complete API key.
        </p>

        {/* 12. WORKFLOW */}
        <h2 style={styles.sectionTitle}>12. WORKFLOW (Flow) request</h2>
        <p style={styles.p}>
          In addition to SMS, RCS and WHATSAPP, customers may submit a configured Flow campaign through the same
          Customer Send API endpoint. Use <span style={styles.inlineCode}>SERVICETYPE = WORKFLOW</span> and provide the
          exact configured flow name in <span style={styles.inlineCode}>TEMPLATENAME</span>. The remaining request
          structure and authentication stay the same.
        </p>
        <pre style={styles.code}>{`POST https://onespace.fabmediatech.com/api/customer/create-data
x-api-key: YOUR_API_KEY
Content-Type: application/json

{
  "VER": "1.0",
  "SERVICETYPE": "WORKFLOW",
  "TEMPLATENAME": "businessworkflow",
  "STATICVARS": "",
  "isblacklistfilter": false,
  "DATA": [
    { "M": "917888722060", "V": "Nishant~Delhi" },
    { "M": "919876543210", "V": "Rahul~Mumbai" }
  ]
}`}</pre>
        <p style={styles.p}>
          The flow name must exist and be available to the authenticated customer. The existing 1-200 recipient limit,
          international phone number format, sequential <span style={styles.inlineCode}>~</span> variable mapping, and
          DLR URL requirements also apply. A successful API response means the request was accepted for asynchronous
          processing, not that the entire flow has completed.
        </p>

        {/* 13. customerparamater */}
        <h2 style={styles.sectionTitle}>13. Optional customerparamater field (all four services)</h2>
        <p style={styles.p}>
          Customers may optionally supply <span style={styles.inlineCode}>customerparamater</span> as a JSON object
          containing their own reference information. This field is supported in request examples for SMS, RCS,
          WHATSAPP and WORKFLOW. Preserve the exact spelling{" "}
          <span style={styles.inlineCode}>customerparamater</span> in requests and callbacks.
        </p>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Field</th>
              <th style={styles.th}>Required</th>
              <th style={styles.th}>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={styles.tdMono}>customerparamater</td>
              <td style={styles.td}>No</td>
              <td style={styles.td}>Customer-defined JSON object (e.g. orderId, source). Omit when not needed.</td>
            </tr>
          </tbody>
        </table>
        <pre style={styles.code}>{`{
  "VER": "1.0",
  "SERVICETYPE": "SMS",
  "TEMPLATENAME": "OTPLOGIN",
  "STATICVARS": "",
  "isblacklistfilter": false,
  "dlr_url": "https://client.example.com/webhooks/message-dlr",
  "customerparamater": {
    "orderId": "098890",
    "source": "TESTANOOP"
  },
  "DATA": [
    { "M": "916280572771", "V": "Nishant~Delhi" }
  ]
}`}</pre>
        <p style={styles.p}>
          For RCS, WHATSAPP or WORKFLOW, replace only{" "}
          <span style={styles.inlineCode}>SERVICETYPE</span> and{" "}
          <span style={styles.inlineCode}>TEMPLATENAME</span> with the appropriate service and approved template /
          configured flow name. The <span style={styles.inlineCode}>customerparamater</span> object is optional for
          each service.
        </p>

        {/* 14. DLR callback with customerparamater */}
        <h2 style={styles.sectionTitle}>14. DLR callback with customerparamater</h2>
        <p style={styles.p}>
          When <span style={styles.inlineCode}>customerparamater</span> is supplied in the send request, the delivery
          report should include the same object alongside its existing DLR fields. Do not replace or rename existing
          status, recipient, request, timestamp or provider metadata fields. If{" "}
          <span style={styles.inlineCode}>customerparamater</span> was not supplied, existing callback behavior
          remains unchanged.
        </p>

        <h3 style={styles.subsectionTitle}>Example: SMS DLR with customerparamater</h3>
        <pre style={styles.code}>{`{
  "apireceive_id": 1001,
  "request_id": "11111111-2222-4333-8444-555555555555",
  "errorcode": "",
  "date": "2026-09-18T09:00:00.000Z",
  "stat": "DELIVRD",
  "phno": "+916280572771",
  "customerparamater": {
    "orderId": "098890",
    "source": "TESTANOOP"
  }
}`}</pre>
        <p style={styles.p}>
          RCS: Add <span style={styles.inlineCode}>customerparamater</span> to the existing RCS DLR JSON (Message,
          Apirequest_id, request_id, Status, Ph_no, UpdatedAt, Error, Timestamp). WhatsApp: Add it to the existing
          callback JSON without altering entry, changes, message_id, status or customer_id. WORKFLOW: Include it with
          each applicable delivery report from the flow, preserving the underlying channel DLR structure and
          correlation identifiers.
        </p>
        <div style={styles.callout}>
          <strong>Implementation note:</strong> This document describes the requested API enhancement. Server-side
          support must save the <span style={styles.inlineCode}>customerparamater</span> object with the request and
          retrieve it when generating DLR callbacks for all four service types. WORKFLOW routing, service permissions
          and callback behavior must also be enabled and tested before describing these examples as
          production-supported.
        </div>

        <hr style={styles.divider} />
        <p style={{ ...styles.p, textAlign: "center", color: "#94A3B8", fontSize: "0.75rem" }}>
          End of customer integration guide
        </p>
      </div>
    </div>
  );
};

export default ApiGuidePage;